import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDate, fmtNum } from "@/lib/api";
import { Panel } from "@/components/common";

export const AutoRemindersPanel = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/settings/auto-reminders").then((r) => setOn(r.data.enabled)); }, []);
  const toggle = async (v) => {
    try { const r = await api.put("/settings/auto-reminders", { enabled: v }); setOn(r.data.enabled); toast.success(`Automatic supplier reminders ${v ? "on" : "off"}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Automatic supplier reminders" testid="auto-reminders-panel" className="mt-5">
      <label className="flex items-center justify-between gap-4 text-sm text-slate-700">
        <span>When On, suppliers with an open invite marked "auto" get a reminder email every 7 days (max 3). When Off, no automatic reminder emails are sent; manual reminders still work.</span>
        <Switch data-testid="auto-reminders-switch" disabled={on === null} checked={!!on} onCheckedChange={toggle} />
      </label>
      <p className="mt-2 text-xs text-slate-500" data-testid="auto-reminders-status">Automatic supplier reminders: {on === null ? "…" : on ? "On" : "Off"}</p>
    </Panel>
  );
};

export const StockRepairPanel = () => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = () => api.get("/housekeeping/stock-repair").then((r) => setP(r.data));
  useEffect(() => { preview(); }, []);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/housekeeping/stock-repair"); toast.success(`Stock flags repaired: ${fmtNum(r.data.changed)} material record(s) updated; ${fmtNum(r.data.in_stock_in_scope_after)} in-stock in-scope`); preview(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Repair stock flags" testid="stock-repair-panel" className="mt-5">
      <p className="text-sm text-slate-600">Recomputes each material&apos;s stock from the Stock upload (summed over storage locations; plants without stock rows use the materials file&apos;s Unrestricted Stock). SAP number formats and leading zeros in material numbers are handled.</p>
      {p && <p className="mt-2 text-sm text-slate-800" data-testid="stock-repair-preview">Preview: {fmtNum(p.changed)} material record(s) would change · in-stock in-scope {fmtNum(p.in_stock_in_scope_before)} → <b>{fmtNum(p.in_stock_in_scope_after)}</b> · {fmtNum(p.stock_rows)} stock rows ({p.stock_plants.join(", ") || "none"})</p>}
      <Button className="mt-3" data-testid="stock-repair-btn" disabled={!p || busy} onClick={run}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Repair stock flags</Button>
    </Panel>
  );
};

export const RecalculatePanel = () => {
  const [s, setS] = useState(null);
  const load = () => api.get("/housekeeping/recalculate").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (s?.status !== "running") return undefined; const t = setInterval(load, 3000); return () => clearInterval(t); }, [s?.status]);
  const run = async () => {
    try { const r = await api.post("/housekeeping/recalculate"); setS(r.data); toast.success("Recalculation started"); } catch (e) { toast.error(errMsg(e)); }
  };
  const running = s?.status === "running";
  return (
    <Panel title="Recalculate now" testid="recalculate-panel" className="mt-5">
      <p className="text-sm text-slate-600">Rebuilds the supplier list from PO lines, re-resolves PO plants and re-classifies PO lines. Restarts and republishing never do this; uploads and settings changes update what they affect.</p>
      <div className="mt-4 flex items-center gap-3">
        <Button data-testid="recalculate-btn" disabled={!s || running} onClick={run}>{running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Recalculate now</Button>
        <span className="text-xs text-slate-500" data-testid="recalculate-status">
          {!s?.status ? "Never run" : running ? `Running since ${fmtDate(s.started_at, true)}` : s.status === "failed" ? `Failed: ${s.error}` : `Last run ${fmtDate(s.finished_at, true)} by ${s.by} · ${s.suppliers} supplier candidates`}
        </span>
      </div>
    </Panel>
  );
};
